import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, downloadFile } from "@/lib/api";
import { fmtDate, fmtMoney, accountLabel } from "@/lib/format";
import { toast } from "sonner";
import { Search, Download, Check, X, Wand2, ChevronLeft, ChevronRight, ChevronDown, Trash2, AlertCircle, Plus, Pencil, Copy, Upload, Repeat, Play, Calendar, History } from "lucide-react";
import { useRowDensity } from "@/lib/useRowDensity";
import RowDensityToggle from "@/components/RowDensityToggle";
import EntityHistoryDrawer from "@/components/EntityHistoryDrawer";

const PAGE_SIZE = 50;

export default function Transactions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [cats, setCats] = useState({ income: [], cogs: [], expense: [], other: [] });
  const [filters, setFilters] = useState(() => ({
    bank_account_id: searchParams.get("bank_account_id") || "",
    category: searchParams.get("category") || "",
    type: searchParams.get("type") || "",
    start: searchParams.get("start") || "",
    end: searchParams.get("end") || "",
    search: searchParams.get("search") || "",
  }));
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const { cfg: densityCfg } = useRowDensity();
  const [bulkCat, setBulkCat] = useState("");
  const [bulkApplying, setBulkApplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | { ids: string[], mode: "single"|"bulk", description?: string }
  const [deleting, setDeleting] = useState(false);
  const [newCat, setNewCat] = useState(null); // null | { name, group, saving, error, assignToTxId? }
  const [newTx, setNewTx] = useState(null);   // null | { mode: "create"|"edit", txId?, form, saving, error }
  const [csvImport, setCsvImport] = useState(null); // null | { bank_account_id, raw, rows, warnings, saving, error, selected }
  const [recurring, setRecurring] = useState(null); // null | { mode, ruleId?, form?, rules?, suggestions?, previews?, expandedPreview?, saving, error, minConfidence? }
  const [historyTx, setHistoryTx] = useState(null); // null | {id, label} — opens the audit-trail drawer for one txn

  // GL drill-down support — `?focus=<txn_id>` highlights + scrolls to a specific row
  const focusId = searchParams.get("focus") || "";
  const [focusActive, setFocusActive] = useState(!!focusId);
  const focusRowRef = useRef(null);

  const allCats = useMemo(() => [...cats.income, ...cats.cogs, ...cats.expense, ...cats.other], [cats]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + items.length);

  const load = async (pageOverride = page) => {
    setLoading(true);
    try {
      // When drilling in via `?focus=<id>`, fetch ONLY that txn so the user
      // lands on the exact row regardless of their saved filters.
      const params = focusId
        ? { id: focusId, limit: 1, skip: 0, paginated: true }
        : {
            ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
            limit: PAGE_SIZE,
            skip: pageOverride * PAGE_SIZE,
            paginated: true,
          };
      const r = await api.get("/transactions", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      if (focusId && (r.data.items || []).length === 0) {
        toast.error("Transaction not found — it may have been deleted or belongs to another tenant.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([api.get("/bank-accounts"), api.get("/categories")]);
      setAccounts(a.data);
      setCats(c.data);
      load(0);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 0 whenever filters change
  useEffect(() => {
    setPage(0);
    load(0);
    /* eslint-disable-next-line */
  }, [filters]);

  // Load when page changes (after initial mount)
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [page]);

  // Reflect filter state in URL so links to /app/transactions?type=income work
  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (focusId) params.focus = focusId;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line
  }, [filters]);

  // Scroll + highlight when a GL drill-down ID is focused
  useEffect(() => {
    if (!focusId || !focusActive) return;
    if (focusRowRef.current) {
      focusRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [items, focusId, focusActive]);

  const clearFocus = () => {
    setFocusActive(false);
    // Drop ?focus=... from URL and reload normal paged list
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setSearchParams(params, { replace: true });
    // Force a reload by setting page to 0 — load() reads focusId from params
    // but we've just cleared it, so a plain reload would still key off the
    // stale focusId. Hard-reload the view by hitting load() with no focus.
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/transactions", {
          params: {
            ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
            limit: PAGE_SIZE,
            skip: 0,
            paginated: true,
          },
        });
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setPage(0);
      } finally { setLoading(false); }
    })();
  };

  const updateCat = async (id, category) => {
    try {
      await api.patch(`/transactions/${id}`, { category });
      setItems((arr) => arr.map((t) => t.id === id ? { ...t, category, ai_categorized: false } : t));
      toast.success("Category updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openNewCat = (assignToTxId = null) => setNewCat({ name: "", group: "expense", saving: false, error: "", assignToTxId });
  const submitNewCat = async () => {
    const name = (newCat?.name || "").trim();
    if (!name) { setNewCat((s) => ({ ...s, error: "Name is required" })); return; }
    setNewCat((s) => ({ ...s, saving: true, error: "" }));
    try {
      const r = await api.post("/categories/custom", { name, group: newCat.group });
      const created = r.data;
      setCats((prev) => ({
        ...prev,
        [created.group]: prev[created.group].includes(created.name)
          ? prev[created.group]
          : [...prev[created.group], created.name],
      }));
      toast.success(`Category "${created.name}" created`);
      if (newCat.assignToTxId) {
        await updateCat(newCat.assignToTxId, created.name);
      }
      setNewCat(null);
    } catch (err) {
      setNewCat((s) => ({ ...s, saving: false, error: err?.response?.data?.detail || "Failed to create" }));
    }
  };

  const toggleReconciled = async (t) => {
    try {
      await api.patch(`/transactions/${t.id}`, { is_reconciled: !t.is_reconciled });
      setItems((arr) => arr.map((x) => x.id === t.id ? { ...x, is_reconciled: !t.is_reconciled } : x));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const exportXlsx = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v && ["bank_account_id", "start", "end"].includes(_)));
      const qs = new URLSearchParams(params).toString();
      await downloadFile(`/transactions/export?${qs}`, "transactions.xlsx");
    } catch (err) { toast.error("Export failed"); }
  };

  // ---- Bulk selection ----
  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((t) => t.id)));
  };
  const clearSelection = () => { setSelected(new Set()); setBulkCat(""); };

  const applyBulkRecategorize = async () => {
    if (selected.size === 0 || !bulkCat) return;
    setBulkApplying(true);
    try {
      const r = await api.post("/transactions/bulk-recategorize", {
        transaction_ids: Array.from(selected),
        category: bulkCat,
      });
      toast.success(`Recategorized ${r.data.modified} transaction(s) to "${bulkCat}"`);
      clearSelection();
      load(page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally { setBulkApplying(false); }
  };

  const requestSingleDelete = (t) => setConfirmDelete({
    mode: "single", ids: [t.id], description: t.description,
    reconciled: !!t.is_reconciled,
  });
  const requestBulkDelete = () => {
    if (selected.size === 0) return;
    const anyReconciled = items.some((t) => selected.has(t.id) && t.is_reconciled);
    setConfirmDelete({
      mode: "bulk", ids: Array.from(selected), count: selected.size,
      reconciled: anyReconciled,
    });
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      if (confirmDelete.mode === "single") {
        await api.delete(`/transactions/${confirmDelete.ids[0]}`);
        toast.success("Transaction deleted");
      } else {
        const r = await api.post("/transactions/bulk-delete", { transaction_ids: confirmDelete.ids });
        const skipped = r.data.skipped_reconciled?.length || 0;
        if (skipped > 0) {
          toast.warning(`Deleted ${r.data.deleted} · Skipped ${skipped} reconciled`);
        } else {
          toast.success(`Deleted ${r.data.deleted} transaction(s)`);
        }
        clearSelection();
      }
      setConfirmDelete(null);
      load(page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const openNewTx = () => setNewTx({
    mode: "create",
    form: {
      bank_account_id: accounts[0]?.id || "",
      date: new Date().toISOString().slice(0, 10),
      description: "",
      amount: "",
      type: "expense",
      category: "Uncategorized",
      is_reconciled: false,
      notes: "",
    },
    saving: false,
    error: "",
  });
  const openEditTx = (t) => setNewTx({
    mode: "edit",
    txId: t.id,
    form: {
      bank_account_id: t.bank_account_id,
      date: t.date,
      description: t.description || "",
      amount: t.amount != null ? String(t.amount) : "",
      type: t.type || "expense",
      category: t.category || "Uncategorized",
      is_reconciled: !!t.is_reconciled,
      notes: t.notes || "",
    },
    saving: false,
    error: "",
  });
  const openDuplicateTx = (t) => setNewTx({
    mode: "create",
    form: {
      bank_account_id: t.bank_account_id,
      date: new Date().toISOString().slice(0, 10),
      description: t.description || "",
      amount: t.amount != null ? String(t.amount) : "",
      type: t.type || "expense",
      category: t.category || "Uncategorized",
      is_reconciled: false,
      notes: t.notes || "",
    },
    saving: false,
    error: "",
  });

  // -------- CSV import (paste or upload) --------
  const openCsvImport = () => setCsvImport({
    bank_account_id: filters.bank_account_id || accounts[0]?.id || "",
    raw: "",
    rows: [],
    warnings: [],
    saving: false,
    error: "",
    selected: new Set(),
    templateActive: false,
  });
  // Minimal RFC-4180-ish CSV parser: handles quoted fields, commas inside quotes, escaped quotes.
  const parseCSV = (text) => {
    const lines = [];
    let field = ""; let row = []; let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else { field += c; }
      } else if (c === '"') { inQuotes = true; }
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); lines.push(row); row = []; field = ""; }
      else if (c === "\r") { /* skip */ }
      else { field += c; }
    }
    if (field.length > 0 || row.length > 0) { row.push(field); lines.push(row); }
    return lines.filter((r) => r.some((x) => (x || "").trim().length > 0));
  };
  const normalizeDate = (s) => {
    const v = (s || "").trim();
    if (!v) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    // Try mm/dd/yyyy or dd/mm/yyyy (assume US mm/dd/yyyy)
    const m = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (m) {
      let [_, mo, da, yr] = m;
      if (yr.length === 2) yr = (parseInt(yr, 10) > 50 ? "19" : "20") + yr;
      return `${yr}-${mo.padStart(2, "0")}-${da.padStart(2, "0")}`;
    }
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return null;
  };
  const parsePastedCsv = (raw, template = null) => {
    const rows = parseCSV(raw);
    if (rows.length === 0) return { parsed: [], warnings: ["CSV is empty"], headerRow: null, indices: null };
    // Detect header row
    const header = rows[0].map((h) => (h || "").trim().toLowerCase());
    const hasHeader = header.some((h) => ["date", "description", "amount", "memo", "payee", "debit", "credit"].includes(h));
    const dataRows = hasHeader ? rows.slice(1) : rows;
    const findIdx = (...names) => header.findIndex((h) => names.includes(h));
    let dateIdx, descIdx, amtIdx, debitIdx, creditIdx, catIdx;
    let usedTemplate = false;
    if (template && hasHeader && JSON.stringify(template.headerRow) === JSON.stringify(header)) {
      ({ dateIdx, descIdx, amtIdx, debitIdx, creditIdx, catIdx } = template.indices);
      usedTemplate = true;
    } else if (!hasHeader) {
      dateIdx = 0; descIdx = 1; amtIdx = 2; catIdx = 3;
      debitIdx = -1; creditIdx = -1;
    } else {
      dateIdx = findIdx("date", "posted", "transaction date", "posted date");
      descIdx = findIdx("description", "memo", "payee", "detail");
      amtIdx = findIdx("amount", "value");
      debitIdx = findIdx("debit", "withdrawal");
      creditIdx = findIdx("credit", "deposit");
      catIdx = findIdx("category");
    }
    const warnings = [];
    if (usedTemplate) warnings.push("Using saved column mapping for this bank.");
    if (dateIdx < 0 && !usedTemplate) warnings.push("No date column detected — first column will be used.");
    if (descIdx < 0 && !usedTemplate) warnings.push("No description column detected — second column will be used.");
    if (amtIdx < 0 && (debitIdx < 0 || creditIdx < 0) && !usedTemplate) warnings.push("No amount column detected — third column will be used.");
    const parsed = [];
    dataRows.forEach((r, i) => {
      const dRaw = r[dateIdx >= 0 ? dateIdx : 0];
      const desc = (r[descIdx >= 0 ? descIdx : 1] || "").trim();
      let amt = null;
      if (amtIdx >= 0) {
        amt = parseFloat(String(r[amtIdx] || "").replace(/[$,]/g, ""));
      } else if (debitIdx >= 0 || creditIdx >= 0) {
        const debit = parseFloat(String(r[debitIdx] || "0").replace(/[$,]/g, "")) || 0;
        const credit = parseFloat(String(r[creditIdx] || "0").replace(/[$,]/g, "")) || 0;
        amt = credit - debit;
      } else {
        amt = parseFloat(String(r[2] || "").replace(/[$,]/g, ""));
      }
      const cat = catIdx >= 0 ? (r[catIdx] || "").trim() : "";
      const date = normalizeDate(dRaw);
      const errors = [];
      if (!date) errors.push("date");
      if (!desc) errors.push("description");
      if (!Number.isFinite(amt) || amt === 0) errors.push("amount");
      parsed.push({
        line: i + (hasHeader ? 2 : 1),
        date: date || "",
        description: desc,
        amount: Number.isFinite(amt) ? amt : null,
        category: cat || "Uncategorized",
        errors,
      });
    });
    return {
      parsed,
      warnings,
      headerRow: hasHeader ? header : null,
      indices: { dateIdx, descIdx, amtIdx, debitIdx, creditIdx, catIdx },
      usedTemplate,
    };
  };

  const loadCsvTemplate = (bankAccountId) => {
    if (!bankAccountId) return null;
    try { return JSON.parse(localStorage.getItem(`ll_csv_template_${bankAccountId}`) || "null"); }
    catch { return null; }
  };
  const saveCsvTemplate = (bankAccountId, headerRow, indices) => {
    if (!bankAccountId || !headerRow) return;
    localStorage.setItem(`ll_csv_template_${bankAccountId}`, JSON.stringify({ headerRow, indices }));
  };
  const clearCsvTemplate = (bankAccountId) => {
    if (!bankAccountId) return;
    localStorage.removeItem(`ll_csv_template_${bankAccountId}`);
    setCsvImport((s) => ({ ...s, templateActive: false, warnings: s.warnings.filter((w) => !w.startsWith("Using saved")) }));
    toast.info("Saved column mapping cleared for this bank");
  };
  const onCsvFileChosen = async (file) => {
    if (!file) return;
    const text = await file.text();
    const tpl = loadCsvTemplate(csvImport?.bank_account_id);
    const res = parsePastedCsv(text, tpl);
    setCsvImport((s) => ({ ...s, raw: text, rows: res.parsed, warnings: res.warnings, error: "", selected: new Set(), templateActive: !!res.usedTemplate, lastHeader: res.headerRow, lastIndices: res.indices }));
  };
  const onCsvRawChange = (raw) => {
    if (!raw.trim()) { setCsvImport((s) => ({ ...s, raw, rows: [], warnings: [], error: "", selected: new Set(), templateActive: false })); return; }
    const tpl = loadCsvTemplate(csvImport?.bank_account_id);
    const res = parsePastedCsv(raw, tpl);
    setCsvImport((s) => ({ ...s, raw, rows: res.parsed, warnings: res.warnings, error: "", selected: new Set(), templateActive: !!res.usedTemplate, lastHeader: res.headerRow, lastIndices: res.indices }));
  };
  const onCsvBankChange = (newBankId) => {
    setCsvImport((s) => {
      if (!s.raw.trim()) return { ...s, bank_account_id: newBankId, templateActive: false };
      const tpl = loadCsvTemplate(newBankId);
      const res = parsePastedCsv(s.raw, tpl);
      return { ...s, bank_account_id: newBankId, rows: res.parsed, warnings: res.warnings, selected: new Set(), templateActive: !!res.usedTemplate, lastHeader: res.headerRow, lastIndices: res.indices };
    });
  };
  const toggleCsvRow = (line) => {
    setCsvImport((s) => {
      const next = new Set(s.selected);
      next.has(line) ? next.delete(line) : next.add(line);
      return { ...s, selected: next };
    });
  };
  const toggleAllCsvRows = () => {
    setCsvImport((s) => {
      const validLines = s.rows.filter((r) => r.errors.length === 0).map((r) => r.line);
      const allSelected = validLines.length > 0 && validLines.every((l) => s.selected.has(l));
      return { ...s, selected: allSelected ? new Set() : new Set(validLines) };
    });
  };
  const applyBulkCsvCategory = (category) => {
    if (!category) return;
    setCsvImport((s) => ({
      ...s,
      rows: s.rows.map((r) => (s.selected.has(r.line) ? { ...r, category } : r)),
    }));
  };
  // -------- Recurring rules --------
  const openRecurringFromTx = (t) => {
    // Default next_run = one month after this tx's date.
    const nextRun = (() => {
      try {
        const d = new Date(t.date);
        d.setMonth(d.getMonth() + 1);
        return d.toISOString().slice(0, 10);
      } catch { return new Date().toISOString().slice(0, 10); }
    })();
    setRecurring({
      mode: "create",
      form: {
        bank_account_id: t.bank_account_id,
        description: t.description || "",
        amount: t.amount != null ? String(t.amount) : "",
        category: t.category || "Uncategorized",
        type: t.type || "expense",
        cadence: "monthly",
        next_run: nextRun,
      },
      saving: false,
      error: "",
    });
  };
  const openRecurringManage = async () => {
    setRecurring({ mode: "manage", rules: [], suggestions: [], previews: {}, expandedPreview: null, minConfidence: 0, saving: false, error: "", loading: true });
    try {
      const [r, s] = await Promise.all([
        api.get("/recurring-rules"),
        api.get("/recurring-rules/suggestions").catch(() => ({ data: [] })),
      ]);
      setRecurring({ mode: "manage", rules: r.data || [], suggestions: s.data || [], previews: {}, expandedPreview: null, minConfidence: 0, saving: false, error: "", loading: false });
    } catch (err) {
      setRecurring({ mode: "manage", rules: [], suggestions: [], previews: {}, expandedPreview: null, minConfidence: 0, saving: false, error: err?.response?.data?.detail || "Failed to load rules", loading: false });
    }
  };
  const togglePreview = async (rule) => {
    setRecurring((s) => {
      if (s.expandedPreview === rule.id) return { ...s, expandedPreview: null };
      return { ...s, expandedPreview: rule.id };
    });
    // Fetch preview lazily and cache in `previews[ruleId]`.
    if (recurring?.previews?.[rule.id]) return;
    try {
      const r = await api.get(`/recurring-rules/${rule.id}/preview?count=6`);
      setRecurring((s) => ({ ...s, previews: { ...s.previews, [rule.id]: r.data.runs || [] } }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load preview"); }
  };
  // Invalidate the preview cache when a rule's schedule/skips change.
  const invalidatePreview = (ruleId) => {
    setRecurring((s) => {
      if (!s?.previews?.[ruleId]) return s;
      const next = { ...s.previews };
      delete next[ruleId];
      return { ...s, previews: next };
    });
  };
  const acceptSuggestion = (sug) => {
    setRecurring({
      mode: "create",
      form: {
        bank_account_id: sug.bank_account_id,
        description: sug.description,
        amount: String(sug.amount),
        category: sug.category || "Uncategorized",
        type: sug.type || "expense",
        cadence: sug.cadence,
        next_run: sug.next_run,
      },
      saving: false,
      error: "",
    });
  };
  const addSkipPeriod = async (rule, period) => {
    if (!period || !/^\d{4}-\d{2}$/.test(period)) { toast.error("Pick a month (YYYY-MM)"); return; }
    const next = Array.from(new Set([...(rule.skip_periods || []), period])).sort();
    try {
      await api.patch(`/recurring-rules/${rule.id}`, { skip_periods: next });
      setRecurring((s) => ({ ...s, rules: s.rules.map((r) => r.id === rule.id ? { ...r, skip_periods: next } : r) }));
      invalidatePreview(rule.id);
      toast.success(`Rule paused for ${period}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const removeSkipPeriod = async (rule, period) => {
    const next = (rule.skip_periods || []).filter((p) => p !== period);
    try {
      await api.patch(`/recurring-rules/${rule.id}`, { skip_periods: next });
      setRecurring((s) => ({ ...s, rules: s.rules.map((r) => r.id === rule.id ? { ...r, skip_periods: next } : r) }));
      invalidatePreview(rule.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const submitRecurring = async () => {
    if (!recurring || recurring.mode !== "create") return;
    const f = recurring.form;
    if (!f.description.trim()) { setRecurring((s) => ({ ...s, error: "Description is required" })); return; }
    const amt = parseFloat(f.amount);
    if (!Number.isFinite(amt) || amt === 0) { setRecurring((s) => ({ ...s, error: "Amount must be non-zero" })); return; }
    let signedAmt = amt;
    if (f.type === "expense" && signedAmt > 0) signedAmt = -signedAmt;
    if (f.type === "income" && signedAmt < 0) signedAmt = Math.abs(signedAmt);
    setRecurring((s) => ({ ...s, saving: true, error: "" }));
    try {
      await api.post("/recurring-rules", {
        bank_account_id: f.bank_account_id,
        description: f.description.trim(),
        amount: signedAmt,
        category: f.category,
        type: f.type,
        cadence: f.cadence,
        next_run: f.next_run,
      });
      toast.success(`Recurring rule set · next run ${f.next_run}`);
      setRecurring(null);
    } catch (err) {
      setRecurring((s) => ({ ...s, saving: false, error: err?.response?.data?.detail || "Failed to create rule" }));
    }
  };
  const toggleRuleActive = async (rule) => {
    try {
      await api.patch(`/recurring-rules/${rule.id}`, { active: !rule.active });
      setRecurring((s) => ({ ...s, rules: s.rules.map((r) => r.id === rule.id ? { ...r, active: !r.active } : r) }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const runRuleNow = async (rule) => {
    try {
      const r = await api.post(`/recurring-rules/${rule.id}/run-now`);
      if (r.data.created) toast.success(`Created transaction · next run ${r.data.next_run}`);
      else toast.info(`Transaction already exists — advanced schedule to ${r.data.next_run}`);
      setRecurring((s) => ({ ...s, rules: s.rules.map((x) => x.id === rule.id ? { ...x, next_run: r.data.next_run, times_run: (x.times_run || 0) + 1 } : x) }));
      invalidatePreview(rule.id);
      load(page);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete recurring rule "${rule.description}"?`)) return;
    try {
      await api.delete(`/recurring-rules/${rule.id}`);
      setRecurring((s) => ({ ...s, rules: s.rules.filter((r) => r.id !== rule.id) }));
      toast.success("Rule deleted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submitCsvImport = async () => {
    if (!csvImport) return;
    const validRows = csvImport.rows.filter((r) => r.errors.length === 0);    if (validRows.length === 0) { setCsvImport((s) => ({ ...s, error: "No valid rows to import" })); return; }
    if (!csvImport.bank_account_id) { setCsvImport((s) => ({ ...s, error: "Pick a bank account" })); return; }
    setCsvImport((s) => ({ ...s, saving: true, error: "" }));
    try {
      const r = await api.post("/transactions/bulk-import", {
        bank_account_id: csvImport.bank_account_id,
        rows: validRows.map((row) => ({
          date: row.date,
          description: row.description,
          amount: row.amount,
          category: row.category || "Uncategorized",
        })),
      });
      const skipped = r.data.skipped_duplicates || 0;
      const invalid = csvImport.rows.length - validRows.length;
      const parts = [`Imported ${r.data.inserted}`];
      if (skipped) parts.push(`skipped ${skipped} duplicate(s)`);
      if (invalid) parts.push(`skipped ${invalid} invalid row(s)`);
      toast.success(parts.join(" · "));
      // Save the column mapping for this bank so re-imports are one-click.
      if (csvImport.lastHeader && csvImport.lastIndices) {
        saveCsvTemplate(csvImport.bank_account_id, csvImport.lastHeader, csvImport.lastIndices);
      }
      setCsvImport(null);
      load(0);
      setPage(0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object"
        ? (detail.errors ? detail.errors.join(" · ") : JSON.stringify(detail))
        : (detail || "Import failed");
      setCsvImport((s) => ({ ...s, saving: false, error: msg }));
    }
  };
  const submitNewTx = async () => {
    if (!newTx) return;
    const f = newTx.form;
    if (!f.bank_account_id) { setNewTx((s) => ({ ...s, error: "Bank account is required" })); return; }
    if (!f.date) { setNewTx((s) => ({ ...s, error: "Date is required" })); return; }
    if (!f.description.trim()) { setNewTx((s) => ({ ...s, error: "Description is required" })); return; }
    const amt = parseFloat(f.amount);
    if (!Number.isFinite(amt) || amt === 0) { setNewTx((s) => ({ ...s, error: "Amount must be a non-zero number" })); return; }
    // Sign convention — flip if it doesn't match the chosen type.
    let signedAmt = amt;
    if (f.type === "expense" && signedAmt > 0) signedAmt = -signedAmt;
    if (f.type === "income" && signedAmt < 0) signedAmt = Math.abs(signedAmt);
    setNewTx((s) => ({ ...s, saving: true, error: "" }));
    try {
      if (newTx.mode === "edit" && newTx.txId) {
        await api.patch(`/transactions/${newTx.txId}`, {
          bank_account_id: f.bank_account_id,
          date: f.date,
          description: f.description.trim(),
          amount: signedAmt,
          category: f.category || "Uncategorized",
          type: f.type,
          is_reconciled: f.is_reconciled,
          notes: f.notes || null,
        });
        toast.success("Transaction updated");
      } else {
        await api.post("/transactions", {
          bank_account_id: f.bank_account_id,
          date: f.date,
          description: f.description.trim(),
          amount: signedAmt,
          category: f.category || "Uncategorized",
          type: f.type,
          is_reconciled: f.is_reconciled,
          notes: f.notes || null,
        });
        toast.success("Transaction added");
      }
      setNewTx(null);
      load(page);
    } catch (err) {
      setNewTx((s) => ({ ...s, saving: false, error: err?.response?.data?.detail || "Failed to save" }));
    }
  };

  return (
    <>
      <PageHeader
        title="Transactions"
        subtitle="Ledger entries"
        testid="transactions-header"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={openNewTx}
              className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5"
              data-testid="new-transaction-btn"
              disabled={accounts.length === 0}
              title={accounts.length === 0 ? "Add a bank account first" : "Add a manual transaction"}
            >
              <Plus size={14} /> <span className="hidden sm:inline">New transaction</span><span className="sm:hidden">New</span>
            </button>
            <button
              onClick={openCsvImport}
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              data-testid="csv-import-btn"
              disabled={accounts.length === 0}
              style={{ borderColor: "var(--ll-border)" }}
              title={accounts.length === 0 ? "Add a bank account first" : "Import many transactions from CSV"}
            >
              <Upload size={14} /> <span className="hidden sm:inline">Import CSV</span><span className="sm:hidden">CSV</span>
            </button>
            <button
              onClick={openRecurringManage}
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
              data-testid="recurring-manage-btn"
              style={{ borderColor: "var(--ll-border)" }}
              title="Manage recurring transactions"
            >
              <Repeat size={14} /> Recurring
            </button>
            <button onClick={exportXlsx} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5" style={{ borderColor: "var(--ll-border)" }} data-testid="transactions-export-btn">
              <Download size={14} /> <span className="hidden sm:inline">Export </span>Excel
            </button>
            {/* Row-density segmented control — hidden on phones (card layout
                there instead of a dense table) */}
            <RowDensityToggle testid="transactions-density" />
          </div>
        }
      />
      <PageBody>
        {loading && <div className="ll-progress mb-4" />}

        {focusActive && focusId && (
          <div
            className="mb-4 border-l-4 border-[#0b5d9e] bg-[#EFF6FF] px-4 py-3 flex items-center justify-between"
            data-testid="focus-banner"
          >
            <div className="text-sm">
              <strong>Showing one transaction</strong> — opened from the General Ledger drill-down.
            </div>
            <button
              type="button"
              onClick={clearFocus}
              data-testid="clear-focus"
              className="text-xs px-3 py-1.5 bg-[#0b5d9e] text-white hover:opacity-90"
            >
              Back to full list
            </button>
          </div>
        )}

        <div className="ll-card-flat p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3" data-testid="transactions-filters">
          <select
            value={filters.bank_account_id} onChange={(e) => setFilters({ ...filters, bank_account_id: e.target.value })}
            className="px-3 py-2 border bg-white text-sm min-w-0" style={{ borderColor: "var(--ll-border)" }} data-testid="filter-account"
          >
            <option value="">All accounts</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{accountLabel(a)}</option>)}
          </select>
          <div className="flex gap-1 min-w-0">
            <select
              value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="flex-1 min-w-0 px-3 py-2 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="filter-category"
            >
              <option value="">All categories</option>
              {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              type="button"
              onClick={() => openNewCat()}
              title="Create new category"
              data-testid="new-category-btn"
              className="px-2 border bg-white hover:bg-[var(--ll-mint)] shrink-0"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Plus size={14} />
            </button>
          </div>
          <select
            value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="px-3 py-2 border bg-white text-sm min-w-0" style={{ borderColor: "var(--ll-border)" }} data-testid="filter-type"
          >
            <option value="">All types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
            <option value="transfer">Transfer</option>
          </select>
          <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="px-3 py-2 border text-sm min-w-0" style={{ borderColor: "var(--ll-border)" }} data-testid="filter-start" />
          <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="px-3 py-2 border text-sm min-w-0" style={{ borderColor: "var(--ll-border)" }} data-testid="filter-end" />
          <div className="relative min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input placeholder="Search description" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="w-full pl-9 pr-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="filter-search" />
          </div>
        </div>

        <div className="ll-card-flat overflow-x-auto">
          {selected.size > 0 && (
            <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3 bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }} data-testid="bulk-action-bar">
              <span className="text-sm font-medium">
                <span className="font-mono-fin">{selected.size}</span> selected
              </span>
              <span className="text-xs text-[var(--ll-ink-soft)]">Recategorize to:</span>
              <select
                value={bulkCat}
                onChange={(e) => {
                  if (e.target.value === "__new__") { openNewCat(); return; }
                  setBulkCat(e.target.value);
                }}
                data-testid="bulk-cat-select"
                className="px-3 py-1.5 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">Choose category…</option>
                {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                <option disabled>──────────</option>
                <option value="__new__">+ New category…</option>
              </select>
              <button
                onClick={applyBulkRecategorize}
                disabled={!bulkCat || bulkApplying}
                data-testid="bulk-apply-btn"
                className="text-sm px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
              >
                <Wand2 size={13} /> {bulkApplying ? "Applying…" : "Apply"}
              </button>
              <button
                onClick={requestBulkDelete}
                data-testid="bulk-delete-btn"
                className="text-sm px-3 py-1.5 border border-[var(--ll-error)] text-[var(--ll-error)] hover:bg-red-50 flex items-center gap-1.5"
              >
                <Trash2 size={13} /> Delete
              </button>
              <button
                onClick={clearSelection}
                data-testid="bulk-clear-btn"
                className="ml-auto text-xs text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)] flex items-center gap-1"
              >
                <X size={13} /> Clear selection
              </button>
            </div>
          )}
          <table className={`w-full ${densityCfg.txt} hidden sm:table`} data-testid="transactions-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                <th className={`px-3 w-8 ${densityCfg.tdY}`}>
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleAll}
                    data-testid="select-all-checkbox"
                    className="accent-[var(--ll-ink)]"
                  />
                </th>
                <th className={`px-4 ${densityCfg.tdY} font-medium`}>Date</th>
                <th className={`px-4 ${densityCfg.tdY} font-medium`}>Description</th>
                <th className={`px-4 ${densityCfg.tdY} font-medium`}>Category</th>
                <th className={`px-4 ${densityCfg.tdY} font-medium`}>Type</th>
                <th className={`px-4 ${densityCfg.tdY} font-medium text-right`}>Amount</th>
                <th className={`px-4 ${densityCfg.tdY} font-medium text-center`}>Reconciled</th>
                <th className={`px-4 ${densityCfg.tdY} font-medium text-center w-10`} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="transactions-empty">No transactions match these filters.</td></tr>
              )}
              {items.map((t) => {
                const isFocus = focusActive && t.id === focusId;
                return (
                <tr
                  key={t.id}
                  ref={isFocus ? focusRowRef : null}
                  className={`border-b transition-colors ${selected.has(t.id) ? "bg-[#0b5d9e] text-white hover:bg-[#0a4f87]" : isFocus ? "bg-amber-100 ring-2 ring-amber-500" : "hover:bg-[#E0F2FE]"}`}
                  style={{ borderColor: "var(--ll-border)" }}
                  data-testid={`tx-row-${t.id}`}
                >
                  <td className={`px-3 ${densityCfg.tdY}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleSelected(t.id)}
                      data-testid={`tx-select-${t.id}`}
                      className={selected.has(t.id) ? "accent-white" : "accent-[var(--ll-ink)]"}
                    />
                  </td>
                  <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-xs whitespace-nowrap`}>{fmtDate(t.date)}</td>
                  <td className={`px-4 ${densityCfg.tdY} max-w-md truncate`} title={t.description}>
                    {t.description}
                    {t.is_manual && (
                      <span
                        className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5"
                        style={{ background: "var(--ll-mint)", color: "var(--ll-ink)" }}
                        data-testid={`tx-manual-badge-${t.id}`}
                      >
                        Manual
                      </span>
                    )}
                  </td>
                  <td className={`px-4 ${densityCfg.tdY}`}>
                    <select
                      value={t.category}
                      onChange={(e) => {
                        if (e.target.value === "__new__") { openNewCat(t.id); return; }
                        updateCat(t.id, e.target.value);
                      }}
                      className="px-2 py-1 border text-xs bg-white" style={{ borderColor: "var(--ll-border)" }}
                      data-testid={`tx-category-${t.id}`}
                    >
                      {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                      <option disabled>──────────</option>
                      <option value="__new__" data-testid={`tx-category-new-${t.id}`}>+ New category…</option>
                    </select>
                    {t.ai_categorized && <span className="ml-2 text-[9px] uppercase tracking-widest text-[var(--ll-ochre)]">AI</span>}
                  </td>
                  <td className={`px-4 ${densityCfg.tdY} text-xs uppercase tracking-wider`}>{t.type}</td>
                  <td className={`px-4 ${densityCfg.tdY} font-mono-fin text-right ${t.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>{fmtMoney(t.amount)}</td>
                  <td className={`px-4 ${densityCfg.tdY} text-center`}>
                    <button onClick={() => toggleReconciled(t)} className={`w-6 h-6 border grid place-items-center ${t.is_reconciled ? "text-white" : ""}`} style={{ borderColor: t.is_reconciled ? "#0b5d9e" : "var(--ll-border)", background: t.is_reconciled ? "#0b5d9e" : "transparent" }} data-testid={`tx-reconcile-${t.id}`}>
                      {t.is_reconciled && <Check size={12} />}
                    </button>
                  </td>
                  <td className={`px-2 ${densityCfg.tdY} text-center`}>
                    <div className="inline-flex items-center gap-0.5">
                      <button
                        onClick={() => openEditTx(t)}
                        title="Edit transaction"
                        data-testid={`tx-edit-${t.id}`}
                        className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] hover:bg-[var(--ll-mint)] transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => openDuplicateTx(t)}
                        title="Duplicate transaction (use today's date)"
                        data-testid={`tx-duplicate-${t.id}`}
                        className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] hover:bg-[var(--ll-mint)] transition-colors"
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        onClick={() => openRecurringFromTx(t)}
                        title="Make this a recurring transaction"
                        data-testid={`tx-recurring-${t.id}`}
                        className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] hover:bg-[var(--ll-mint)] transition-colors"
                      >
                        <Repeat size={14} />
                      </button>
                      <button
                        onClick={() => setHistoryTx({ id: t.id, label: `${t.description} · ${fmtMoney(t.amount)}` })}
                        title="View change history"
                        data-testid={`tx-history-${t.id}`}
                        className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] hover:bg-[var(--ll-mint)] transition-colors"
                      >
                        <History size={14} />
                      </button>
                      <button
                        onClick={() => requestSingleDelete(t)}
                        disabled={t.is_reconciled}
                        title={t.is_reconciled ? "Un-reconcile first" : "Delete transaction"}
                        data-testid={`tx-delete-${t.id}`}
                        className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-error)] hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[var(--ll-ink-muted)]"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile card list — stacked rows that stay thumb-friendly on
              phones. Shows the same core data as the table, minus the
              per-row 4-icon action strip (which collapses to an action menu
              on tap). */}
          <ul className="sm:hidden divide-y" style={{ borderColor: "var(--ll-border)" }} data-testid="transactions-mobile-list">
            {items.length === 0 && (
              <li className="px-4 py-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="transactions-mobile-empty">
                No transactions match these filters.
              </li>
            )}
            {items.map((t) => {
              const isSel = selected.has(t.id);
              const isFocus = focusActive && t.id === focusId;
              return (
                <li
                  key={t.id}
                  ref={isFocus ? focusRowRef : null}
                  className={`px-4 py-3 ${isSel ? "bg-[#0b5d9e] text-white" : isFocus ? "bg-amber-100" : "bg-white"}`}
                  data-testid={`tx-mobile-row-${t.id}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleSelected(t.id)}
                      data-testid={`tx-mobile-select-${t.id}`}
                      className={`mt-1 ${isSel ? "accent-white" : "accent-[var(--ll-ink)]"}`}
                      aria-label="Select transaction"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className={`text-[10px] uppercase tracking-widest font-mono-fin ${isSel ? "text-white/80" : "text-[var(--ll-ink-muted)]"}`}>
                            {fmtDate(t.date)} · {t.type}
                          </div>
                          <div className="font-medium text-sm leading-snug mt-0.5 break-words" title={t.description}>
                            {t.description}
                            {t.is_manual && (
                              <span
                                className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${isSel ? "bg-white/20" : ""}`}
                                style={isSel ? {} : { background: "var(--ll-mint)", color: "var(--ll-ink)" }}
                              >Manual</span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`font-mono-fin text-base font-medium shrink-0 ${isSel ? "" : (t.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]")}`}
                        >
                          {fmtMoney(t.amount)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <select
                          value={t.category}
                          onChange={(e) => {
                            if (e.target.value === "__new__") { openNewCat(t.id); return; }
                            updateCat(t.id, e.target.value);
                          }}
                          className="px-2 py-1 border text-xs bg-white text-[var(--ll-ink)]"
                          style={{ borderColor: "var(--ll-border)" }}
                          data-testid={`tx-mobile-category-${t.id}`}
                        >
                          {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                          <option disabled>──────────</option>
                          <option value="__new__">+ New category…</option>
                        </select>
                        {t.ai_categorized && (
                          <span className={`text-[9px] uppercase tracking-widest ${isSel ? "text-white/80" : "text-[var(--ll-ochre)]"}`}>AI</span>
                        )}
                        <button
                          onClick={() => toggleReconciled(t)}
                          data-testid={`tx-mobile-reconcile-${t.id}`}
                          className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${t.is_reconciled ? "bg-white text-[#0b5d9e] border-white" : (isSel ? "border-white/50 text-white" : "border-[var(--ll-border)] text-[var(--ll-ink-soft)]")}`}
                        >
                          {t.is_reconciled ? <><Check size={10} /> Reconciled</> : "Not reconciled"}
                        </button>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        <button onClick={() => openEditTx(t)} data-testid={`tx-mobile-edit-${t.id}`} aria-label="Edit" className={`p-2 border ${isSel ? "border-white/40" : "border-[var(--ll-border)]"}`}><Pencil size={13} /></button>
                        <button onClick={() => openDuplicateTx(t)} data-testid={`tx-mobile-duplicate-${t.id}`} aria-label="Duplicate" className={`p-2 border ${isSel ? "border-white/40" : "border-[var(--ll-border)]"}`}><Copy size={13} /></button>
                        <button onClick={() => openRecurringFromTx(t)} data-testid={`tx-mobile-recurring-${t.id}`} aria-label="Make recurring" className={`p-2 border ${isSel ? "border-white/40" : "border-[var(--ll-border)]"}`}><Repeat size={13} /></button>
                        <button onClick={() => setHistoryTx({ id: t.id, label: `${t.description} · ${fmtMoney(t.amount)}` })} data-testid={`tx-mobile-history-${t.id}`} aria-label="View history" className={`p-2 border ${isSel ? "border-white/40" : "border-[var(--ll-border)]"}`}><History size={13} /></button>
                        <button
                          onClick={() => requestSingleDelete(t)}
                          disabled={t.is_reconciled}
                          data-testid={`tx-mobile-delete-${t.id}`}
                          aria-label="Delete"
                          className={`p-2 border ${isSel ? "border-white/40" : "border-[var(--ll-border)]"} disabled:opacity-30`}
                        ><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Pagination footer */}
        {total > 0 && (
          <div
            className="ll-card-flat mt-3 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
            data-testid="transactions-pagination"
          >
            <div className="text-xs text-[var(--ll-ink-soft)] font-mono-fin uppercase tracking-widest">
              Showing <span className="text-[var(--ll-ink)]">{showingFrom}–{showingTo}</span> of <span className="text-[var(--ll-ink)]">{total.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                data-testid="tx-page-prev"
                className="text-sm px-3 py-1.5 border hover:bg-[var(--ll-mint)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="text-xs px-3 font-mono-fin" data-testid="tx-page-indicator">
                Page {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                data-testid="tx-page-next"
                className="text-sm px-3 py-1.5 border hover:bg-[var(--ll-mint)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="delete-confirm-modal">
            <div className="bg-white max-w-md w-full border shadow-xl" style={{ borderColor: "var(--ll-border)" }}>
              <div className="px-5 py-4 border-b flex items-start gap-3" style={{ borderColor: "var(--ll-border)" }}>
                <div className="w-9 h-9 rounded-full grid place-items-center shrink-0" style={{ background: "#FEF2F2", color: "var(--ll-error)" }}>
                  <AlertCircle size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Confirm delete</div>
                  <div className="font-display font-bold text-lg mt-0.5">
                    {confirmDelete.mode === "single"
                      ? "Delete this transaction?"
                      : `Delete ${confirmDelete.count} transaction${confirmDelete.count > 1 ? "s" : ""}?`}
                  </div>
                </div>
              </div>
              <div className="px-5 py-4 text-sm text-[var(--ll-ink-soft)] space-y-2">
                {confirmDelete.mode === "single" && (
                  <p>
                    <span className="font-mono-fin text-[var(--ll-ink)]">{confirmDelete.description}</span> will be permanently removed. This can&apos;t be undone.
                  </p>
                )}
                {confirmDelete.mode === "bulk" && (
                  <p>
                    All selected transactions will be permanently removed. This can&apos;t be undone.
                    {confirmDelete.reconciled && (
                      <span className="block mt-2 text-[var(--ll-ochre)] text-xs">
                        Reconciled transactions in the selection will be skipped automatically.
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={() => setConfirmDelete(null)}
                  disabled={deleting}
                  data-testid="delete-cancel-btn"
                  className="text-sm px-4 py-2 border hover:bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={performDelete}
                  disabled={deleting}
                  data-testid="delete-confirm-btn"
                  className="text-sm px-4 py-2 bg-[var(--ll-error)] text-white hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-60"
                >
                  <Trash2 size={13} /> {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {recurring && recurring.mode === "create" && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => !recurring.saving && setRecurring(null)}
            data-testid="recurring-create-modal"
          >
            <div className="bg-white w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                <div>
                  <div className="text-sm font-medium">Make transaction recurring</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] mt-0.5">
                    Auto-creates a copy on schedule
                  </div>
                </div>
                <button onClick={() => !recurring.saving && setRecurring(null)} className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]" aria-label="Close"><X size={14} /></button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Description</label>
                  <input
                    value={recurring.form.description}
                    onChange={(e) => setRecurring((s) => ({ ...s, form: { ...s.form, description: e.target.value }, error: "" }))}
                    data-testid="recurring-description"
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Type</label>
                    <select
                      value={recurring.form.type}
                      onChange={(e) => setRecurring((s) => ({ ...s, form: { ...s.form, type: e.target.value } }))}
                      data-testid="recurring-type"
                      className="w-full px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      <option value="income">Income</option>
                      <option value="expense">Expense</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={recurring.form.amount}
                      onChange={(e) => setRecurring((s) => ({ ...s, form: { ...s.form, amount: e.target.value } }))}
                      data-testid="recurring-amount"
                      className="w-full px-3 py-2 border text-sm font-mono-fin"
                      style={{ borderColor: "var(--ll-border)" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Category</label>
                    <select
                      value={recurring.form.category}
                      onChange={(e) => setRecurring((s) => ({ ...s, form: { ...s.form, category: e.target.value } }))}
                      data-testid="recurring-category"
                      className="w-full px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Cadence</label>
                    <select
                      value={recurring.form.cadence}
                      onChange={(e) => setRecurring((s) => ({ ...s, form: { ...s.form, cadence: e.target.value } }))}
                      data-testid="recurring-cadence"
                      className="w-full px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Next run</label>
                    <input
                      type="date"
                      value={recurring.form.next_run}
                      onChange={(e) => setRecurring((s) => ({ ...s, form: { ...s.form, next_run: e.target.value } }))}
                      data-testid="recurring-next-run"
                      className="w-full px-3 py-2 border text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    />
                  </div>
                </div>
                <p className="text-[10px] text-[var(--ll-ink-soft)]">
                  A daily job (5am UTC) creates a new transaction on each Next Run and advances the schedule by the cadence. Duplicates on the same (date, description, amount) are skipped safely.
                </p>
                {recurring.error && (
                  <div className="text-xs text-[var(--ll-error)] flex items-center gap-1.5" data-testid="recurring-error">
                    <AlertCircle size={12} /> {recurring.error}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={() => setRecurring(null)}
                  disabled={recurring.saving}
                  data-testid="recurring-cancel"
                  className="text-sm px-4 py-2 border hover:bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRecurring}
                  disabled={recurring.saving}
                  data-testid="recurring-save"
                  className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
                >
                  <Repeat size={13} /> {recurring.saving ? "Saving…" : "Set recurring"}
                </button>
              </div>
            </div>
          </div>
        )}

        {recurring && recurring.mode === "manage" && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setRecurring(null)}
            data-testid="recurring-manage-modal"
          >
            <div className="bg-white w-full max-w-3xl shadow-xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                <div>
                  <div className="text-sm font-medium">Recurring rules</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] mt-0.5">
                    Auto-created transactions run daily at 5am UTC
                  </div>
                </div>
                <button onClick={() => setRecurring(null)} className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]" aria-label="Close"><X size={14} /></button>
              </div>
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
                {recurring.loading && <div className="ll-progress" />}
                {!recurring.loading && recurring.suggestions && recurring.suggestions.length > 0 && (
                  <div className="border" style={{ borderColor: "var(--ll-border)" }} data-testid="recurring-suggestions">
                    <div className="px-3 py-2 bg-[var(--ll-mint)] border-b text-[10px] uppercase tracking-widest text-[var(--ll-ink)] flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--ll-border)" }}>
                      <span className="flex items-center gap-1.5">
                        <Wand2 size={11} />
                        Suggested rules · {recurring.suggestions.filter((s) => (s.confidence || 0) >= (recurring.minConfidence || 0)).length} shown of {recurring.suggestions.length} spotted
                      </span>
                      <label className="flex items-center gap-1.5 normal-case tracking-normal text-[10px] text-[var(--ll-ink-soft)]">
                        Hide low confidence
                        <select
                          value={recurring.minConfidence || 0}
                          onChange={(e) => setRecurring((s) => ({ ...s, minConfidence: Number(e.target.value) }))}
                          data-testid="recurring-suggest-confidence-filter"
                          className="px-1 py-0.5 border bg-white"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          <option value={0}>Show all</option>
                          <option value={50}>≥ 50%</option>
                          <option value={70}>≥ 70%</option>
                          <option value={85}>≥ 85%</option>
                        </select>
                      </label>
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-white border-b" style={{ borderColor: "var(--ll-border)" }}>
                          <th className="px-3 py-2 font-medium">Description</th>
                          <th className="px-3 py-2 font-medium">Account</th>
                          <th className="px-3 py-2 font-medium">Cadence</th>
                          <th className="px-3 py-2 font-medium text-right">Amount</th>
                          <th className="px-3 py-2 font-medium text-center">Seen</th>
                          <th className="px-3 py-2 font-medium text-center">Confidence</th>
                          <th className="px-3 py-2 font-medium text-right w-24" aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {recurring.suggestions
                          .filter((s) => (s.confidence || 0) >= (recurring.minConfidence || 0))
                          .map((s, i) => {
                            const conf = s.confidence || 0;
                            const confColor = conf >= 85 ? "var(--ll-success)" : conf >= 60 ? "var(--ll-ochre)" : "var(--ll-error)";
                            const confLabel = conf >= 85 ? "High" : conf >= 60 ? "Med" : "Low";
                            return (
                              <tr
                                key={`${s.bank_account_id}-${i}`}
                                className="border-b hover:bg-[var(--ll-bg-sidebar)]"
                                style={{ borderColor: "var(--ll-border)" }}
                                data-testid={`recurring-suggestion-${i}`}
                              >
                                <td className="px-3 py-1.5 truncate max-w-[14rem]" title={s.description}>{s.description}</td>
                                <td className="px-3 py-1.5 text-[var(--ll-ink-soft)] text-[10px]">{accounts.find((a) => a.id === s.bank_account_id)?.name || "—"}</td>
                                <td className="px-3 py-1.5 uppercase tracking-wider">{s.cadence}</td>
                                <td className={`px-3 py-1.5 font-mono-fin text-right ${s.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>{fmtMoney(s.amount)}</td>
                                <td className="px-3 py-1.5 text-center font-mono-fin">{s.occurrences}×</td>
                                <td className="px-3 py-1.5 text-center" data-testid={`recurring-suggest-confidence-${i}`}>
                                  <div
                                    className="inline-flex flex-col items-center gap-0.5"
                                    title={`${conf}% · mean ${s.mean_delta_days}d ± ${s.stddev_days}d`}
                                  >
                                    <span className="text-[10px] font-medium uppercase tracking-widest" style={{ color: confColor }}>{confLabel}</span>
                                    <div className="w-12 h-1 bg-[var(--ll-bg-sidebar)] overflow-hidden">
                                      <div className="h-full" style={{ width: `${conf}%`, background: confColor }} />
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  <button
                                    onClick={() => acceptSuggestion(s)}
                                    data-testid={`recurring-accept-${i}`}
                                    className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] inline-flex items-center gap-1"
                                  >
                                    <Plus size={10} /> Create
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
                {!recurring.loading && recurring.rules.length === 0 && (
                  <div className="text-center py-8 text-sm text-[var(--ll-ink-soft)]" data-testid="recurring-empty">
                    No recurring rules yet. Click the <Repeat size={12} className="inline" /> icon on any transaction row to make it recurring.
                  </div>
                )}
                {recurring.rules.length > 0 && (
                  <table className="w-full text-xs" data-testid="recurring-rules-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                        <th className="px-3 py-2 font-medium">Description</th>
                        <th className="px-3 py-2 font-medium">Account</th>
                        <th className="px-3 py-2 font-medium">Cadence</th>
                        <th className="px-3 py-2 font-medium">Next run</th>
                        <th className="px-3 py-2 font-medium text-right">Amount</th>
                        <th className="px-3 py-2 font-medium text-center">Active</th>
                        <th className="px-3 py-2 font-medium">Skipped</th>
                        <th className="px-3 py-2 font-medium text-right w-32" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {recurring.rules.map((rule) => {
                        const isExpanded = recurring.expandedPreview === rule.id;
                        const preview = recurring.previews?.[rule.id];
                        return (
                        <Fragment key={rule.id}>
                          <tr
                            className="border-b hover:bg-[var(--ll-bg-sidebar)]"
                            style={{ borderColor: "var(--ll-border)" }}
                            data-testid={`recurring-rule-${rule.id}`}
                          >
                            <td className="px-3 py-2 truncate max-w-[14rem]" title={rule.description}>{rule.description}</td>
                          <td className="px-3 py-2 text-[var(--ll-ink-soft)]">{accounts.find((a) => a.id === rule.bank_account_id)?.name || "—"}</td>
                          <td className="px-3 py-2 uppercase tracking-wider">{rule.cadence}</td>
                          <td className="px-3 py-2 font-mono-fin">
                            <button
                              onClick={() => togglePreview(rule)}
                              data-testid={`recurring-preview-toggle-${rule.id}`}
                              className="inline-flex items-center gap-1 hover:text-[var(--ll-ink)] text-[var(--ll-ink-soft)]"
                              title="Preview upcoming runs"
                            >
                              {rule.next_run}
                              {isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                            </button>
                          </td>
                          <td className={`px-3 py-2 font-mono-fin text-right ${rule.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]"}`}>{fmtMoney(rule.amount)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => toggleRuleActive(rule)}
                              data-testid={`recurring-toggle-${rule.id}`}
                              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${rule.active ? "bg-[var(--ll-mint)] text-[var(--ll-ink)]" : "bg-[var(--ll-bg-sidebar)] text-[var(--ll-ink-soft)]"}`}
                            >
                              {rule.active ? "On" : "Off"}
                            </button>
                          </td>
                          <td className="px-3 py-2" data-testid={`recurring-skips-${rule.id}`}>
                            <div className="flex flex-wrap items-center gap-1">
                              {(rule.skip_periods || []).map((p) => (
                                <span
                                  key={p}
                                  className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-[var(--ll-ochre)]/20 text-[var(--ll-ochre)]"
                                  data-testid={`recurring-skip-${rule.id}-${p}`}
                                >
                                  {p}
                                  <button
                                    onClick={() => removeSkipPeriod(rule, p)}
                                    className="ml-0.5 hover:text-[var(--ll-error)]"
                                    aria-label={`Remove skip ${p}`}
                                  >
                                    <X size={9} />
                                  </button>
                                </span>
                              ))}
                              <input
                                type="month"
                                onChange={(e) => { if (e.target.value) { addSkipPeriod(rule, e.target.value); e.target.value = ""; } }}
                                data-testid={`recurring-skip-add-${rule.id}`}
                                className="text-[10px] px-1 py-0.5 border w-[6.5rem]"
                                style={{ borderColor: "var(--ll-border)" }}
                                title="Add a month to pause this rule"
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex items-center gap-0.5">
                              <button
                                onClick={() => runRuleNow(rule)}
                                title="Run now (create the next transaction)"
                                data-testid={`recurring-runnow-${rule.id}`}
                                className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] hover:bg-[var(--ll-mint)] transition-colors"
                              >
                                <Play size={13} />
                              </button>
                              <button
                                onClick={() => deleteRule(rule)}
                                title="Delete rule"
                                data-testid={`recurring-delete-${rule.id}`}
                                className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[var(--ll-error)] hover:bg-red-50 transition-colors"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                          </tr>
                          {isExpanded && (
                            <tr className="border-b bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`recurring-preview-row-${rule.id}`}>
                              <td colSpan={8} className="px-3 py-3">
                                <div className="flex items-start gap-3">
                                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] shrink-0 mt-1">
                                    <Calendar size={11} />
                                    Next 6 runs
                                  </div>
                                  <div className="flex-1 flex flex-wrap gap-1.5" data-testid={`recurring-preview-chips-${rule.id}`}>
                                    {!preview && <span className="text-[10px] text-[var(--ll-ink-soft)] italic">Loading…</span>}
                                    {preview && preview.length === 0 && <span className="text-[10px] text-[var(--ll-ink-soft)] italic">No upcoming runs.</span>}
                                    {preview && preview.map((run, i) => (
                                      <span
                                        key={run.date}
                                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 font-mono-fin ${run.skipped ? "bg-[var(--ll-ochre)]/15 text-[var(--ll-ochre)] line-through decoration-[var(--ll-ochre)]" : "bg-white text-[var(--ll-ink)] border"}`}
                                        style={!run.skipped ? { borderColor: "var(--ll-border)" } : undefined}
                                        data-testid={`recurring-preview-chip-${rule.id}-${i}`}
                                        title={run.skipped ? `Skipped — ${run.date.slice(0, 7)} is in the pause list` : `Next run — ${run.date}`}
                                      >
                                        {run.date}
                                        {run.skipped && <span className="uppercase tracking-widest">skip</span>}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={() => setRecurring(null)}
                  data-testid="recurring-close"
                  className="text-sm px-4 py-2 border hover:bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {csvImport && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => !csvImport.saving && setCsvImport(null)}
            data-testid="csv-import-modal"
          >
            <div className="bg-white w-full max-w-3xl shadow-xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                <div>
                  <div className="text-sm font-medium">Import transactions from CSV</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] mt-0.5">
                    Paste rows or drop a file · max 500 rows · duplicates auto-skipped
                  </div>
                </div>
                <button onClick={() => !csvImport.saving && setCsvImport(null)} className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]" aria-label="Close"><X size={14} /></button>
              </div>
              <div className="px-5 py-4 space-y-4 overflow-y-auto">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Target bank account</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={csvImport.bank_account_id}
                      onChange={(e) => onCsvBankChange(e.target.value)}
                      data-testid="csv-import-account"
                      className="flex-1 px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {accounts.map((a) => <option key={a.id} value={a.id}>{accountLabel(a)}</option>)}
                    </select>
                    {csvImport.templateActive && (
                      <button
                        onClick={() => clearCsvTemplate(csvImport.bank_account_id)}
                        data-testid="csv-import-clear-template"
                        className="text-xs px-2 py-2 border hover:bg-white text-[var(--ll-ink-soft)]"
                        style={{ borderColor: "var(--ll-border)" }}
                        title="Clear the saved column mapping for this bank"
                      >
                        Reset mapping
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Paste CSV</label>
                  <textarea
                    value={csvImport.raw}
                    onChange={(e) => onCsvRawChange(e.target.value)}
                    placeholder={"date,description,amount,category\n2025-06-01,Stripe payout,5000.00,Sales Revenue\n2025-06-02,AWS,-120.50,Software & Subscriptions"}
                    rows={6}
                    data-testid="csv-import-textarea"
                    className="w-full px-3 py-2 border font-mono-fin text-xs"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <label className="text-xs text-[var(--ll-ink-soft)] inline-flex items-center gap-1.5 cursor-pointer hover:text-[var(--ll-ink)]">
                      <Upload size={13} />
                      <span>Or upload a .csv file</span>
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => onCsvFileChosen(e.target.files?.[0])}
                        data-testid="csv-import-file"
                        className="hidden"
                      />
                    </label>
                    <div className="text-[10px] text-[var(--ll-ink-muted)]">
                      Columns: date, description, amount, category (optional). Positive = income, negative = expense.
                    </div>
                  </div>
                </div>
                {csvImport.warnings.length > 0 && (
                  <div className="text-xs text-[var(--ll-ochre)] flex items-start gap-1.5" data-testid="csv-import-warnings">
                    <AlertCircle size={12} className="mt-0.5 shrink-0" />
                    <div>{csvImport.warnings.join(" · ")}</div>
                  </div>
                )}
                {csvImport.rows.length > 0 && (
                  <div className="border" style={{ borderColor: "var(--ll-border)" }}>
                    <div className="px-3 py-2 bg-[var(--ll-bg-sidebar)] border-b text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                      <span data-testid="csv-import-preview-summary">
                        Preview: {csvImport.rows.filter((r) => r.errors.length === 0).length} valid · {csvImport.rows.filter((r) => r.errors.length > 0).length} invalid
                      </span>
                      <span>Showing first 20</span>
                    </div>
                    {csvImport.selected.size > 0 && (
                      <div
                        className="px-3 py-2 border-b bg-[var(--ll-mint)] flex flex-wrap items-center gap-3"
                        style={{ borderColor: "var(--ll-border)" }}
                        data-testid="csv-bulk-cat-bar"
                      >
                        <span className="text-xs font-medium">
                          <span className="font-mono-fin">{csvImport.selected.size}</span> selected
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]">Assign category:</span>
                        <select
                          onChange={(e) => {
                            if (e.target.value === "__new__") { openNewCat(); e.target.value = ""; return; }
                            applyBulkCsvCategory(e.target.value);
                            e.target.value = "";
                          }}
                          defaultValue=""
                          data-testid="csv-bulk-cat-select"
                          className="text-xs px-2 py-1 border bg-white"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          <option value="" disabled>Choose category…</option>
                          {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                          <option disabled>──────────</option>
                          <option value="__new__">+ New category…</option>
                        </select>
                        <button
                          onClick={() => setCsvImport((s) => ({ ...s, selected: new Set() }))}
                          data-testid="csv-bulk-clear"
                          className="ml-auto text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
                        >
                          Clear selection
                        </button>
                      </div>
                    )}
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-xs" data-testid="csv-import-preview-table">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-white border-b" style={{ borderColor: "var(--ll-border)" }}>
                            <th className="px-3 py-2 font-medium w-8">
                              <input
                                type="checkbox"
                                checked={
                                  csvImport.rows.filter((r) => r.errors.length === 0).length > 0 &&
                                  csvImport.rows.filter((r) => r.errors.length === 0).every((r) => csvImport.selected.has(r.line))
                                }
                                onChange={toggleAllCsvRows}
                                data-testid="csv-select-all"
                                className="accent-[var(--ll-ink)]"
                              />
                            </th>
                            <th className="px-3 py-2 font-medium w-10">Line</th>
                            <th className="px-3 py-2 font-medium">Date</th>
                            <th className="px-3 py-2 font-medium">Description</th>
                            <th className="px-3 py-2 font-medium text-right">Amount</th>
                            <th className="px-3 py-2 font-medium">Category</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvImport.rows.slice(0, 20).map((r) => (
                            <tr
                              key={r.line}
                              className={`border-b ${r.errors.length > 0 ? "bg-red-50" : (csvImport.selected.has(r.line) ? "bg-[var(--ll-mint)]" : "")}`}
                              style={{ borderColor: "var(--ll-border)" }}
                              data-testid={`csv-import-row-${r.line}`}
                            >
                              <td className="px-3 py-1.5">
                                {r.errors.length === 0 && (
                                  <input
                                    type="checkbox"
                                    checked={csvImport.selected.has(r.line)}
                                    onChange={() => toggleCsvRow(r.line)}
                                    data-testid={`csv-row-select-${r.line}`}
                                    className="accent-[var(--ll-ink)]"
                                  />
                                )}
                              </td>
                              <td className="px-3 py-1.5 font-mono-fin text-[var(--ll-ink-muted)]">{r.line}</td>
                              <td className={`px-3 py-1.5 font-mono-fin ${r.errors.includes("date") ? "text-[var(--ll-error)]" : ""}`}>{r.date || "—"}</td>
                              <td className={`px-3 py-1.5 truncate max-w-xs ${r.errors.includes("description") ? "text-[var(--ll-error)]" : ""}`}>{r.description || "—"}</td>
                              <td className={`px-3 py-1.5 font-mono-fin text-right ${r.errors.includes("amount") ? "text-[var(--ll-error)]" : (r.amount != null && r.amount < 0 ? "text-[var(--ll-error)]" : "text-[var(--ll-success)]")}`}>
                                {r.amount != null ? fmtMoney(r.amount) : "—"}
                              </td>
                              <td className="px-3 py-1.5">{r.category}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {csvImport.error && (
                  <div className="text-xs text-[var(--ll-error)] flex items-center gap-1.5" data-testid="csv-import-error">
                    <AlertCircle size={12} /> {csvImport.error}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={() => setCsvImport(null)}
                  disabled={csvImport.saving}
                  data-testid="csv-import-cancel"
                  className="text-sm px-4 py-2 border hover:bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitCsvImport}
                  disabled={csvImport.saving || csvImport.rows.filter((r) => r.errors.length === 0).length === 0}
                  data-testid="csv-import-submit"
                  className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
                >
                  <Upload size={13} /> {csvImport.saving ? "Importing…" : `Import ${csvImport.rows.filter((r) => r.errors.length === 0).length} row(s)`}
                </button>
              </div>
            </div>
          </div>
        )}

        {newTx && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => !newTx.saving && setNewTx(null)}
            data-testid={newTx.mode === "edit" ? "edit-transaction-modal" : "new-transaction-modal"}
          >
            <div className="bg-white w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                <div className="text-sm font-medium">
                  {newTx.mode === "edit" ? "Edit transaction" : "Add manual transaction"}
                </div>
                <button onClick={() => !newTx.saving && setNewTx(null)} className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]" aria-label="Close"><X size={14} /></button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Bank account</label>
                    <select
                      value={newTx.form.bank_account_id}
                      onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, bank_account_id: e.target.value }, error: "" }))}
                      data-testid="new-tx-account"
                      className="w-full px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {accounts.map((a) => <option key={a.id} value={a.id}>{accountLabel(a)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Date</label>
                    <input
                      type="date"
                      value={newTx.form.date}
                      onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, date: e.target.value }, error: "" }))}
                      data-testid="new-tx-date"
                      className="w-full px-3 py-2 border text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Description</label>
                  <input
                    autoFocus
                    value={newTx.form.description}
                    onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, description: e.target.value }, error: "" }))}
                    placeholder="e.g. Stripe payout · March"
                    data-testid="new-tx-description"
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Type</label>
                    <select
                      value={newTx.form.type}
                      onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, type: e.target.value }, error: "" }))}
                      data-testid="new-tx-type"
                      className="w-full px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      <option value="income">Income</option>
                      <option value="expense">Expense</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newTx.form.amount}
                      onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, amount: e.target.value }, error: "" }))}
                      placeholder="0.00"
                      data-testid="new-tx-amount"
                      className="w-full px-3 py-2 border text-sm font-mono-fin"
                      style={{ borderColor: "var(--ll-border)" }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Category</label>
                    <select
                      value={newTx.form.category}
                      onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, category: e.target.value }, error: "" }))}
                      data-testid="new-tx-category"
                      className="w-full px-3 py-2 border bg-white text-sm"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {allCats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-[var(--ll-ink-soft)]">
                  <input
                    type="checkbox"
                    checked={newTx.form.is_reconciled}
                    onChange={(e) => setNewTx((s) => ({ ...s, form: { ...s.form, is_reconciled: e.target.checked } }))}
                    data-testid="new-tx-reconciled"
                    className="accent-[var(--ll-ink)]"
                  />
                  Mark as reconciled immediately
                </label>
                <p className="text-[10px] text-[var(--ll-ink-soft)]">
                  Sign convention: positive amounts are income, negative are expense. We'll flip the sign automatically if it doesn't match the chosen type.
                </p>
                {newTx.error && (
                  <div className="text-xs text-[var(--ll-error)] flex items-center gap-1.5" data-testid="new-tx-error">
                    <AlertCircle size={12} /> {newTx.error}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={() => setNewTx(null)}
                  disabled={newTx.saving}
                  data-testid="new-tx-cancel"
                  className="text-sm px-4 py-2 border hover:bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitNewTx}
                  disabled={newTx.saving}
                  data-testid="new-tx-save"
                  className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
                >
                  {newTx.mode === "edit"
                    ? (newTx.saving ? "Saving…" : "Save changes")
                    : (<><Plus size={13} /> {newTx.saving ? "Adding…" : "Add transaction"}</>)}
                </button>
              </div>
            </div>
          </div>
        )}

        {newCat && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => !newCat.saving && setNewCat(null)}
            data-testid="new-category-modal"
          >
            <div
              className="bg-white w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                <div className="text-sm font-medium">Create custom category</div>
                <button
                  onClick={() => !newCat.saving && setNewCat(null)}
                  className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Name</label>
                  <input
                    autoFocus
                    value={newCat.name}
                    onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value, error: "" }))}
                    onKeyDown={(e) => { if (e.key === "Enter" && !newCat.saving) submitNewCat(); }}
                    placeholder="e.g. Client Gifts"
                    data-testid="new-category-name"
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Group</label>
                  <select
                    value={newCat.group}
                    onChange={(e) => setNewCat((s) => ({ ...s, group: e.target.value }))}
                    data-testid="new-category-group"
                    className="w-full px-3 py-2 border bg-white text-sm"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="income">Income</option>
                    <option value="cogs">Cost of Goods Sold</option>
                    <option value="expense">Expense</option>
                    <option value="other">Other</option>
                  </select>
                  <p className="text-[10px] text-[var(--ll-ink-soft)] mt-1">
                    Groups drive which section of the P&amp;L this category rolls up into.
                  </p>
                </div>
                {newCat.error && (
                  <div className="text-xs text-[var(--ll-error)] flex items-center gap-1.5" data-testid="new-category-error">
                    <AlertCircle size={12} /> {newCat.error}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  onClick={() => setNewCat(null)}
                  disabled={newCat.saving}
                  data-testid="new-category-cancel"
                  className="text-sm px-4 py-2 border hover:bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitNewCat}
                  disabled={newCat.saving || !newCat.name.trim()}
                  data-testid="new-category-save"
                  className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
                >
                  <Plus size={13} /> {newCat.saving ? "Creating…" : "Create category"}
                </button>
              </div>
            </div>
          </div>
        )}
      </PageBody>

      <EntityHistoryDrawer
        open={!!historyTx}
        onClose={() => setHistoryTx(null)}
        entityType="transaction"
        entityId={historyTx?.id}
        entityLabel={historyTx?.label}
      />
    </>
  );
}
